import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { Select, Input, Checkbox, Empty, Flex } from "antd"
import { SearchOutlined, CaretDownOutlined } from "@ant-design/icons"
import styles from "./EcperienceSelector.module.css"
import { Text } from "ui/Typography"
 
const ExperienceSelector = ({ value = [], isView, onChange, experiences = [], onOpen }) => {
  const normalizedValue = useMemo(() => value.map(String), [value])
  const [searchText, setSearchText] = useState("")
  const [isOpen, setIsOpen] = useState(false)
 
  const normalizedSearch = searchText.trim().toLowerCase()
 
  const filteredExperiences = useMemo(() => {
    if (!normalizedSearch) return experiences
    return experiences.filter((exp) => (exp.name || "").toLowerCase().includes(normalizedSearch))
  }, [experiences, normalizedSearch])
 
  const filteredIds = useMemo(() => filteredExperiences.filter((exp) => exp.waiver_count !== 5).map((exp) => String(exp.id)), [filteredExperiences])
 
  const areAllFilteredSelected = useMemo(() => {
    return filteredIds.length > 0 && filteredIds.every((id) => normalizedValue.includes(id))
  }, [filteredIds, normalizedValue])
 
  const handleSelectAll = (checked) => {
    if (checked) {
      const newValue = [...new Set([...normalizedValue, ...filteredIds])]
      onChange(newValue)
    } else {
      // Unselect only the currently filtered experiences, keep the rest
      const newValue = normalizedValue.filter((id) => !filteredIds.includes(id))
      onChange(newValue)
    }
  }
 
  const handleExperienceToggle = (expId, checked) => {
    const expIdStr = String(expId)
    let newValue
    if (checked) {
      newValue = [...normalizedValue, expIdStr]
    } else {
      newValue = normalizedValue.filter((id) => id !== expIdStr)
    }
    onChange(newValue)
  }
 
  const dropdownRender = (_menu) => (
    <section
      className={styles.experienceDropdown}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
    >
      <div className={`${styles.searchContainer} p-2 border-b`}>
        <Input
          allowClear
          placeholder="Search experiences..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>
      <div className={`${styles.selectAll} p-2 border-b`}>
        <Checkbox
          checked={areAllFilteredSelected}
          // indeterminate={
          //   !areAllFilteredSelected &&
          //   filteredIds.some(id => normalizedValue.includes(id))
          // }
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          Select All
        </Checkbox>
      </div>
      <div
        className={styles.experienceList}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
        tabIndex={-1}
      >
        {filteredExperiences.length > 0 ? (
          filteredExperiences.map((exp) => {
            const expIdStr = String(exp.id)
            const isSelected = normalizedValue.includes(expIdStr)
            const isDisabled = exp.waiver_count === 5
            const STATUS_LABELS = { published: "Published", unpublished: "Unpublished" }
            const statusLabel = STATUS_LABELS[exp.status] ?? "Draft"
            return (
              <div key={expIdStr} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={`${styles.experienceItem} ${isDisabled ? styles.experienceItemDisabled : ""}`}
                  disabled={isDisabled}
                  onClick={() => handleExperienceToggle(expIdStr, !isSelected)}
                >
                  <Checkbox checked={isSelected} disabled={isDisabled} onChange={(e) => handleExperienceToggle(expIdStr, e.target.checked)} onClick={(e) => e.stopPropagation()} />
                  <Flex align="center" wrap style={{ flex: 1 }}>
                    <Text size="caption">
                      {exp.name}
                      <Text
                        size="caption"
                        color={exp.status === "published" ? "var(--color-success)" : "var(--color-text-secondary)"}
                        fw="500"
                        className={styles.statusLabel}
                      >
                        ({statusLabel})
                      </Text>
                      <Text
                        size="caption"
                        color={isDisabled ? "var(--color-error)" : "var(--color-text-secondary)"}
                        fw="500"
                        className={styles.statusLabel}
                      >
                        ({exp.waiver_count ?? 0}/5 waivers)
                      </Text>
                    </Text>
                  </Flex>
                </button>
              </div>
            )
          })
        ) : (
          <div className="p-2 text-gray-500">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No experiences found" />
          </div>
        )}
      </div>
  </section>
  )
 
  const options = experiences.map((exp) => ({ label: exp.name, value: String(exp.id) }))
  return (
    <Select
    disabled={isView}
      mode="multiple"
      placeholder="Select Experience"
      value={normalizedValue}
      showSearch={false}
      onChange={(vals) => onChange(vals.map(String))}
      dropdownRender={dropdownRender}
      suffixIcon={<CaretDownOutlined />}
      options={options}
      className={styles.experienceSelect}
      tagRender={() => <></>}
      open={isOpen}
      onDropdownVisibleChange={(open) => {
        setIsOpen(open)
        if (open) onOpen?.()
        if (!open) setSearchText("")
      }}
    />
  )
 
}
 
// Custom compare: shallow compare value array contents & experiences length + ids
const areEqual = (prevProps, nextProps) => {
  const prevVals = prevProps?.value?.map(String)
  const nextVals = nextProps?.value?.map(String)
  if (prevVals?.length !== nextVals?.length) return false
  for (let i = 0; i < prevVals?.length; i++) {
    if (prevVals[i] !== nextVals[i]) return false
  }
  const prevExps = prevProps?.experiences
  const nextExps = nextProps?.experiences
  if (prevExps?.length !== nextExps?.length) return false
  for (let i = 0; i < prevExps.length; i++) {
    if (String(prevExps[i].id) !== String(nextExps[i].id)) return false
    if (prevExps[i].name !== nextExps[i].name) return false
    if (prevExps[i].status !== nextExps[i].status) return false
    if (prevExps[i].waiver_count !== nextExps[i].waiver_count) return false
  }
  return true
}
 
 
ExperienceSelector.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  isView: PropTypes.bool,
  experiences: PropTypes.array,
  onOpen: PropTypes.func,
}
 
export default React.memo(ExperienceSelector, areEqual)